import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll to top on every route change (window + any ".page-scroll" containers).
 * Also disables browser's back/forward scroll restoration for consistency on mobile.
 */
export default function ScrollManager({ selector = ".page-scroll" }) {
  const { pathname, search, hash } = useLocation();

  // Disable native scroll restoration (Android & iOS Safari quirk)
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => { window.history.scrollRestoration = prev || "auto"; };
    }
  }, []);

  useEffect(() => {
    // blur any focused input to avoid iOS keyboard nudging scroll
    if (document.activeElement?.blur) document.activeElement.blur();

    const containers = Array.from(document.querySelectorAll(selector));
    const appEl = document.getElementById("app-scroll");
    if (appEl) containers.push(appEl);

    // reset window & document scroll first
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // reset known scrollable containers
    containers.forEach((el) => (el.scrollTop = 0));

    // iOS Safari occasionally needs one extra frame after paint
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        containers.forEach((el) => (el.scrollTop = 0));
        window.scrollTo(0, 0);
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [pathname, search, hash, selector]);

  return null;
}
